import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, useTheme, Modal, TextField, MenuItem, Autocomplete } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { useGetApplicationsQuery } from "../../services/applicationService";
import { applicationsColumns } from "./config";
import { dataGridStyles, localeText } from "../../utils/dataGridConfig";
import ErrorComponent from "../../components/ErrorComponent";
import ApplicationDetailsModal from "../../components/ApplicationDetailsModal";
import dayjs from "dayjs";
import { ticket_types } from "../../constants/enums";
import { useGetLotesQuery } from "../../services/loteService";
import InfoButton from "../../components/InfoButton";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useGetForcesQuery } from "../../services/forceService";

const Applications = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const [selectedApplication, setSelectedApplication] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [filters, setFilters] = useState({
    lote_id: searchParams.get("lote_id") || "",
    force_id: searchParams.get("force_id") || "",
    start_date: searchParams.get("start_date") || "",
    end_date: searchParams.get("end_date") || "",
    type: searchParams.get("type") || "",
    period: searchParams.get("period") || "",
  });

  const { data: lotes } = useGetLotesQuery({ limit:100 }, {
    refetchOnMountOrArgChange: true,
  });

  const { data: forces } = useGetForcesQuery({ 
    limit:100,
    lote_id: filters.lote_id || -1 
  }, {
    refetchOnMountOrArgChange: true,
  });

  const { data: applications, error, isLoading, refetch } = useGetApplicationsQuery({
    offset: paginationModel.page * paginationModel.pageSize,
    limit: paginationModel.pageSize,
    lote_id: filters.lote_id,
    force_id: filters.force_id,
    start_date: filters.start_date,
    end_date: filters.end_date,
    type: filters.type,
  });

  useEffect(() => {
    refetch();
  }, [paginationModel, filters, refetch]);

  if (error) {
    return <ErrorComponent />;
  }

  const handleSeeDosages = async (id) => {
    setSelectedApplication(applications?.records.find(app => app.id === id));
    setIsModalOpen(true);
  };

  const handleView = async (id) => {
    navigate(`${window.location.pathname}/view/${id}`);
  };

  const handleApply = async (id) => {
    navigate(`${window.location.pathname}/apply/${id}`);
  };

  const handleEdit = async (id) => {
    navigate(`${window.location.pathname}/edit/${id}`);
  };

  const columns = applicationsColumns( handleApply, handleEdit, handleSeeDosages, handleView, colors);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedApplication(null);
  };

  const updateURLFilters = (newFilters) => {
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    });
    setSearchParams(params);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const newFilters = {
      ...filters,
      [name]: value,
    };
    setFilters(newFilters);
    updateURLFilters(newFilters);
  };

  const handleDateChange = (name) => (date) => {
    const newFilters = {
      ...filters,
      [name]: date ? dayjs(date).format("YYYY-MM-DD") : null,
    };
    setFilters(newFilters);
    updateURLFilters(newFilters);
  };

  const handlePeriodChange = (e) => {
    const today = dayjs();
    let startDate, endDate;

    switch (e.target.value) {
      case "semanaActual":
        startDate = today.startOf("week").format("YYYY-MM-DD");
        endDate = today.endOf("week").format("YYYY-MM-DD");
        break;
      case "mesActual":
        startDate = today.startOf("month").format("YYYY-MM-DD");
        endDate = today.endOf("month").format("YYYY-MM-DD");
        break;
      case "semanaAnterior":
        startDate = today.subtract(1, "week").startOf("week").format("YYYY-MM-DD");
        endDate = today.subtract(1, "week").endOf("week").format("YYYY-MM-DD");
        break;
      case "semanaSiguiente":
        startDate = today.add(1, "week").startOf("week").format("YYYY-MM-DD");
        endDate = today.add(1, "week").endOf("week").format("YYYY-MM-DD");
        break;
      case "mesAnterior":
        startDate = today.subtract(1, "month").startOf("month").format("YYYY-MM-DD");
        endDate = today.subtract(1, "month").endOf("month").format("YYYY-MM-DD");
        break;
      case "mesSiguiente":
        startDate = today.add(1, "month").startOf("month").format("YYYY-MM-DD");
        endDate = today.add(1, "month").endOf("month").format("YYYY-MM-DD");
        break;
      default:
        startDate = null;
        endDate = null;
        break;
    }

    const newFilters = {
      ...filters,
      period: e.target.value,
      start_date: startDate,
      end_date: endDate,
    };
    setFilters(newFilters);
    updateURLFilters(newFilters);
  };

  const handleClearFilters = () => {
    const newFilters = {
      lote_id: "",
      force_id: "",
      start_date: "",
      end_date: "",
      type: "",
      period: "",
    };
    setFilters(newFilters);
    updateURLFilters(newFilters);
  };

  return (
    <Box m="20px">
      <Header title="Aplicaciones" subtitle="Lista de aplicaciones" showCreateButton={true} />

      {/* Filter Section */}
      <Box
        display="grid"
        gap="30px"
        gridTemplateColumns="repeat(14, minmax(0, 1fr))"
      >
        {/* Lote Autocomplete */}
        <Autocomplete
          options={lotes?.records || []}
          getOptionLabel={(option) => option.name}
          value={
            lotes?.records?.find((lote) => lote.id == filters.lote_id) || null
          }
          onChange={(event, value) => {
            const newFilters = {
              ...filters,
              lote_id: value ? value.id : "",
              force_id: ""
            };
            setFilters(newFilters);
            updateURLFilters(newFilters);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Lote"
              variant="outlined"
              size="small"
              fullWidth
            />
          )}
          sx={{ gridColumn: "span 2" }}
        />

        {/* Lote Autocomplete */}
        <Autocomplete
          disabled={forces?.records.length === 0}
          options={forces?.records || []}
          getOptionLabel={(option) => option.number}
          value={
            forces?.records?.find((force) => force.id == filters.force_id) || null
          }
          onChange={(event, value) => {
            const newFilters = {
              ...filters,
              force_id: value ? value.id : "",
            };
            setFilters(newFilters);
            updateURLFilters(newFilters);
          }}
          renderInput={(params) => (
            <TextField
              disabled={forces?.records.length === 0}
              {...params}
              label="Forza"
              variant="outlined"
              size="small"
              fullWidth
            />
          )}
          sx={{ gridColumn: "span 2" }}
        />

        {/* Type Dropdown */}
        <TextField
          label="Tipo"
          name="type"
          value={filters.type}
          onChange={handleFilterChange}
          select
          variant="outlined"
          size="small"
          fullWidth
          sx={{ gridColumn: "span 2" }}
        >
          {ticket_types.map((type) => (
            <MenuItem key={type.value} value={type.value}>
              {type.label}
            </MenuItem>
          ))}
        </TextField>

        {/* Period Dropdown */}
        <TextField
          select
          label="Periodo"
          name="period"
          value={filters.period}
          onChange={handlePeriodChange}
          variant="outlined"
          size="small"
          fullWidth
          sx={{ gridColumn: "span 2" }}
        >
          <MenuItem value="semanaActual">Semana Actual</MenuItem>
          <MenuItem value="semanaAnterior">Semana Anterior</MenuItem>
          <MenuItem value="semanaSiguiente">Semana Siguiente</MenuItem>
          <MenuItem value="mesActual">Mes Actual</MenuItem>
          <MenuItem value="mesAnterior">Mes Anterior</MenuItem>
          <MenuItem value="mesSiguiente">Mes Siguiente</MenuItem>
        </TextField>

        {/* Start Date Picker */}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Fecha inicial"
            value={filters.start_date ? dayjs(filters.start_date) : null}
            onChange={(value) => handleDateChange("start_date")(value)}
            format="DD/MM/YYYY" // Set user-friendly format
            slotProps={{
              textField: {
                variant: "outlined",
                size: "small",
                fullWidth: true,
                sx: { gridColumn: "span 2" },
              },
            }}
          />
        </LocalizationProvider>

        {/* End Date Picker */}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Fecha final"
            value={filters.end_date ? dayjs(filters.end_date) : null}
            onChange={(value) => handleDateChange("end_date")(value)}
            format="DD/MM/YYYY" // Set user-friendly format
            slotProps={{
              textField: {
                variant: "outlined",
                size: "small",
                fullWidth: true,
                sx: { gridColumn: "span 2" },
              },
            }}
          />
        </LocalizationProvider>

        {/* Clear Filters Button */}
        <Box
          sx={{ 
            gridColumn: "span 2",
            display: "flex",
            alignItems: "center"
          }}
        >
          <InfoButton label="Limpiar Filros" onClick={handleClearFilters} />
        </Box>
      </Box>

      {/* DataGrid Section */}
      <Box sx={dataGridStyles(colors)} height="75vh">
        <DataGrid
          columns={columns}
          rows={applications?.records || []}
          rowCount={applications?.count || 0}
          loading={isLoading}
          pagination
          paginationModel={paginationModel}
          rowsPerPageOptions={[10, 20, 50]}
          pageSizeOptions={[10, 20, 50]}
          paginationMode="server"
          onPaginationModelChange={(newPaginationModel) => {
            setPaginationModel(newPaginationModel);
          }}
          loadingText="Cargando..."
          localeText={localeText}
          disableRowSelectionOnClick
          disableColumnMenu
          disableColumnSorting
        />
      </Box>

      {/* Modal for Application Details */}
      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            width: "100vw",
            height: "100vh",
          }}
        >
          <ApplicationDetailsModal
            applicationId={selectedApplication?.id}
            onClose={handleCloseModal}
          />
        </Box>
      </Modal>
    </Box>
  );
};

export default Applications;